import { setActivePinia } from 'pinia'
import { navigateTo, useNuxtApp } from 'nuxt/app'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { useAppAccess } from '@common/app-access-layer/composables/useAppAccess'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { AUTH_PATH, getMagicLinkAuthUrl } from '@plugins/utils/magic-link'
import {
  appendShowBetaPreviewToUrl,
  getShowBetaPreviewFromUrl,
  stripShowBetaPreviewFromUrl,
} from '@common/beta-preview-layer/utils/show-beta-preview-url.utils'
import { consumeAuthenticatedLoginVisit, CP_AUTH_EXCLUDED_PATHS_SET, CP_AUTH_PUBLIC_FLOW_PATHS_SET } from '../constants/auth.constants'
import { hydrateLocationStoreFromPortalSettingsQuery } from '../utils/hydrate-location-from-portal-settings-cache'
import { buildMagicLinkAuthCpNavigationInput } from '../utils/magic-link-auth-navigation.utils'

import type { Pinia } from 'pinia'
import type { QueryClient } from '@tanstack/vue-query'

/**
 * Authentication Middleware for Clientportal App
 */
export default defineNuxtRouteMiddleware(async to => {
  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined

  if (!pinia) {
    return
  }

  setActivePinia(pinia)

  const authStore = useAuthStore(pinia)
  const isAuthenticated = authStore.isLoggedIn
  const { CPNavigateTo } = useCPNavigation()

  // Magic-link / login-code / session-key forward. The `show-beta-preview` flag is
  // stripped from the URL before computing `redirect=` (so it does not leak into
  // the post-login path) and re-appended to the `/auth?...` URL so it survives the
  // SSR redirect and the beta-preview-layer middleware can hydrate Pinia on /auth.
  // Hydration itself is owned by `@common/beta-preview-layer/middleware/beta-preview.global`.
  const cleanedFullPath = stripShowBetaPreviewFromUrl(to.fullPath)
  const baseAuthUrl = getMagicLinkAuthUrl(cleanedFullPath)
  if (baseAuthUrl) {
    const beta = getShowBetaPreviewFromUrl(to.fullPath)
    const authUrl = beta != null ? appendShowBetaPreviewToUrl(baseAuthUrl, beta) : baseAuthUrl
    return await CPNavigateTo(buildMagicLinkAuthCpNavigationInput(authUrl, { replace: true, redirectCode: 302 }))
  }

  const isExcludedRoute = CP_AUTH_EXCLUDED_PATHS_SET.has(to.path)

  if (isExcludedRoute) {
    // /auth processes a magic-link / login-code / session-key credential client-side
    // and may replace the current session with a different account. Authenticated
    // users MUST be allowed to land here — otherwise we'd bounce them to /dashboard
    // before the page can run.
    if (to.path === AUTH_PATH) return

    if (isAuthenticated) {
      // Already-authenticated visit to /login or /signup with `?redirect=<path>`:
      // honour the redirect target here. Without this branch the user would land on
      // the login form indefinitely — `useAuthPage.handleLoginCallback` only fires
      // on a *fresh* login submit (via the `@login-callback` event), so an authed
      // landing has no other trigger.
      //
      // Same-origin guard mirrors `useAuth.navigateToUrl`: require `startsWith('/')`,
      // reject protocol-relative (`//evil`) and backslash variants (`/\evil`) to
      // prevent open-redirect.
      const isLoginOrSignupPath = to.path === '/login' || to.path === '/signup'
      const rawRedirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
      const isSafeInternalRedirect =
        rawRedirect.length > 0 && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') && !rawRedirect.startsWith('/\\')

      if (isLoginOrSignupPath && isSafeInternalRedirect) {
        // Path-based navigation: CPNavigateTo requires a named route, but `?redirect=`
        // can target any internal path. Use Nuxt's `navigateTo` directly with replace+302.
        return await navigateTo(rawRedirect, { replace: true, redirectCode: 302 })
      }

      // Auth-flow pages (e.g. /reset-password, /forgot-password, /login-with-code)
      // are reachable by authenticated users too — same intent as a magic-link visit
      // ("replace existing session" / "complete an emailed reset link"). The page
      // itself owns the post-action routing, so we let the navigation through.
      if (CP_AUTH_PUBLIC_FLOW_PATHS_SET.has(to.path)) return
      // One-shot bypass: AuthStatePanel "Back to login" (after a magic-link token
      // expired) sets a module flag so an authenticated user can reach /login without
      // being bounced to /dashboard. Consumed exactly once — subsequent navigations to
      // /login still bounce as normal. URL stays clean.
      if (consumeAuthenticatedLoginVisit()) return
      return await CPNavigateTo({ name: 'clientportal-dashboard', options: { replace: true, redirectCode: 302 } })
    }
    return
  }

  if (!isAuthenticated) {
    // Communities-layer pages (groups, users, conversations) are publicly accessible.
    // cpRoute meta is set only on clientportal-layer pages; communities-layer pages lack it.
    if (to.path.startsWith('/communities') && !to.meta.cpRoute) return

    const redirectPath = to.fullPath
    const loginPath = '/login'

    if (redirectPath !== loginPath && !redirectPath.startsWith(loginPath)) {
      return await CPNavigateTo({
        name: 'auth-login',
        query: { redirect: redirectPath },
        options: { replace: true, redirectCode: 302 },
      })
    }

    return await CPNavigateTo({ name: 'auth-login', options: { replace: true, redirectCode: 302 } })
  }

  // ===== APP ACCESS CHECK =====
  try {
    const { getAppIdFromPath, canAccessApp } = useAppAccess()
    const appId = getAppIdFromPath(to.path)

    if (appId) {
      const location = useLocation()

      if (!location.store.hasLocation) {
        await hydrateLocationStoreFromPortalSettingsQuery(nuxtApp.$queryClient as QueryClient | undefined, location)
      }

      if (!canAccessApp(appId)) {
        return await CPNavigateTo({ name: 'clientportal-dashboard', options: { replace: true } })
      }
    }
  } catch (error) {
    console.error('Client portal auth middleware: app access check failed:', error)
    return await CPNavigateTo({ name: 'clientportal-dashboard', options: { replace: true } })
  }
})
